import {
  ArrayElement,
  JsonServiceConditional,
  Service,
  UUIDv4,
} from '@quality24/inpatient-typings';
import {
  CronRule,
  cronCheck,
  validateConditional,
} from '@quality24/react-order-components';
import { ComposedPatientCallReason as HookComposedPatientCallReason } from '@/inpatient-patient-pwa/hooks/useTriggerOrder';
import { ComposedPatientCallReason } from '@/inpatient-patient-pwa/pages/OrderTriggerPage/types';

/**
 * Checks whether a reason can be triggered based on its active alerts. A reason
 * with an active alert (or whose children all have active alerts) is considered
 * "in progress" and should be disabled.
 */
export const canBeTriggered = (reason: ComposedPatientCallReason): boolean => {
  if (reason.children?.length) {
    const activeAlerts = reason.children.filter((child) => {
      const innerChildren = child.hasChildren
        ? child.children?.filter((child2) => !!child2.activeAlert)
        : undefined;
      return !innerChildren
        ? !!child.activeAlert
        : innerChildren?.length === child.children?.length;
    });
    return reason.children.length !== activeAlerts.length;
  }
  return !reason.activeAlert;
};

export interface ReasonSelection {
  conditional: Service['conditional'];
  lastAlert?: { createdAt: string } | null;
  groupId?: UUIDv4;
  hasChildren?: boolean;
  serviceType?: string;
}

/**
 * Validates a selected reason against its conditional rules and resolves any
 * active `alternativeFlow` (cron-based group override), returning the reason
 * ready to be dispatched to `useTriggerOrder` — or `null` when the conditional
 * blocks the selection (a toast has already been emitted in that case).
 * `resolveCronMessage` builds that toast's message in the app's language; see
 * `useReasonSelection`, which wires both callbacks.
 *
 * Shared by the HomePage `OrderButton`, the `OrderTriggerPage` listing and the
 * `select-child` modal so the behaviour stays identical across entry points.
 */
export const resolveReasonSelection = (
  selection: ReasonSelection,
  errorToast?: (message: string) => void,
  resolveCronMessage?: (cronRule: CronRule) => string,
): HookComposedPatientCallReason | null => {
  if (
    !selection.hasChildren &&
    !validateConditional(
      selection.conditional,
      selection.lastAlert?.createdAt || null,
      errorToast,
      resolveCronMessage,
    )
  ) {
    return null;
  }

  // Validate alternative flows (cron-based group override)
  let altFlow:
    | ArrayElement<NonNullable<JsonServiceConditional['alternativeFlows']>>
    | undefined;
  if (
    selection.conditional?.alternativeFlows &&
    selection.conditional.alternativeFlows.length > 0
  ) {
    const currentDate = new Date();
    altFlow = selection.conditional.alternativeFlows.find((flow) =>
      flow.rules.some((rule) => cronCheck(rule, currentDate)),
    );
  }

  return {
    ...selection,
    serviceType: ('serviceType' in selection
      ? selection.serviceType
      : 'admission') as string,
    groupId: altFlow && altFlow.groupId ? altFlow.groupId : selection.groupId,
  } as unknown as HookComposedPatientCallReason;
};
